<template>
  <ion-app>
    <ion-alert :is-open="isDialogOpen" :message="promptMessage"> </ion-alert>
    <TheSideMenu v-if="!isAdminRoute" />
    <ion-router-outlet id="main-content" />
  </ion-app>
</template>

<script>
import { IonApp, IonRouterOutlet, IonAlert } from "@ionic/vue";
import { defineComponent } from "vue";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import TheSideMenu from "@/components/TheSideMenu.vue";
import ThermalPrinter from "@/plugins/thermal-printer";

export default defineComponent({
  name: "App",
  components: {
    IonApp,
    IonRouterOutlet,
    IonAlert,
    TheSideMenu,
  },
  data() {
    return {
      isDialogOpen: false,
      promptMessage: "",
      appStateChangeListener: null, // Store listener reference for cleanup
      resumeListener: null, // Store listener reference for cleanup
    };
  },
  computed: {
    isAdminRoute() {
      return this.$route?.path?.startsWith("/admin");
    },
  },
  created() {
    // Setup app lifecycle listeners for printer service management
    // Must be in created() not mounted() because native appStateChange event
    // fires before Vue finishes mounting
    this.setupPrinterLifecycleHandling();
  },
  beforeUnmount() {
    // Clean up Capacitor event listeners to prevent memory leaks
    if (this.appStateChangeListener) {
      this.appStateChangeListener.remove();
      this.appStateChangeListener = null;
      console.log("Removed appStateChange listener");
    }
    if (this.resumeListener) {
      this.resumeListener.remove();
      this.resumeListener = null;
      console.log("Removed resume listener");
    }
  },
  methods: {
    async setupPrinterLifecycleHandling() {
      // Only handle on Android (ThermalPrinter plugin is Android-only)
      const platform = Capacitor.getPlatform();
      if (platform !== "android") {
        console.log(
          `Printer lifecycle handling skipped on ${platform} platform (Android only)`
        );
        return;
      }

      console.log("Setting up printer lifecycle handling");

      // Don't call forceRebind() on startup - let the plugin bind naturally
      // The plugin's load() method already binds to the print service

      // Just check status after a short delay to allow binding to complete
      setTimeout(async () => {
        try {
          const status = await ThermalPrinter.isReady();
          console.log("Initial printer status:", status);
        } catch (error) {
          console.warn("Could not get initial printer status:", error);
        }
      }, 2000);

      // Listen for app state changes (background/foreground)
      // Store listener reference for cleanup
      this.appStateChangeListener = await CapacitorApp.addListener(
        "appStateChange",
        async ({ isActive }) => {
          console.log("App state changed - isActive:", isActive);

          if (isActive) {
            // App came to foreground - check if we need to rebind
            try {
              const status = await ThermalPrinter.isReady();
              console.log("Printer status on resume:", status);

              // Only rebind if not currently bound
              if (!status.bound) {
                console.log("Printer not bound - attempting rebind");
                const rebindResult = await ThermalPrinter.rebind();
                console.log("Rebind result:", rebindResult);
              }
            } catch (error) {
              console.error("Error checking printer on resume:", error);
            }
          }
        }
      );

      // Also listen for app resume event (additional safety)
      // Store listener reference for cleanup
      this.resumeListener = await CapacitorApp.addListener(
        "resume",
        async () => {
          console.log("App resume event fired");
        }
      );

      console.log("Printer lifecycle handling configured");
    },
  },
});
</script>
